import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  ChevronDown,
  ChevronUp,
  XCircle,
  Send,
  Lock,
  Loader2,
  Link2,
  Users,
} from "lucide-react";
import ParticipantList from "./ParticipantList";
import { resolveMoney } from "@/utils/formatPrice";
import type { ActivityDetail, ActivityPlayer } from "@/hooks/useActivityDetail";
import type { Tables } from "@/integrations/supabase/types";

interface OrganizerPanelProps {
  activity: ActivityDetail;
  players: ActivityPlayer[];
  onApprove: (playerId: string) => void;
  onReject: (playerId: string) => void;
  onRemove: (playerId: string) => void;
  onCancelActivity: () => void;
  onUpdateActivity: (updates: Partial<Tables<"activities">>) => void;
  onConnectCourts?: () => void;
  isApproving: boolean;
  isRejecting: boolean;
  isRemoving: boolean;
  isCancelling: boolean;
  isUpdating: boolean;
}

export default function OrganizerPanel({
  activity,
  players,
  onApprove,
  onReject,
  onRemove,
  onCancelActivity,
  onUpdateActivity,
  onConnectCourts,
  isApproving,
  isRejecting,
  isRemoving,
  isCancelling,
  isUpdating,
}: OrganizerPanelProps) {
  const { t } = useTranslation("activities");
  const [isExpanded, setIsExpanded] = useState(false);
  const [description, setDescription] = useState(activity.description ?? "");
  const [descriptionDirty, setDescriptionDirty] = useState(false);

  const registrationOpen = activity.status === "open" || activity.status === "full";

  const handleSaveDescription = () => {
    onUpdateActivity({ description });
    setDescriptionDirty(false);
  };

  const handleCloseRegistration = () => {
    onUpdateActivity({ status: "closed" as string });
  };

  const handleOpenRegistration = () => {
    onUpdateActivity({ status: "open" as string });
  };

  // Payment mode
  const paymentMode = (activity.payment_mode ?? 'system') as 'system' | 'external' | 'free';

  // G-023: v2 (fee-led) activities carry organizer_fee_per_person_minor (öre) —
  // NULL only on legacy rows (no column default; create_activity_v2 always
  // writes ≥0). Same v2 fields the facit derivation reads. DISPLAY-only.
  const feeMinor = (activity as any).organizer_fee_per_person_minor as number | null | undefined;
  const isV2 = feeMinor !== null && feeMinor !== undefined;
  const feeKr = isV2 ? Math.round(feeMinor / 100) : 0;
  const displayPriceMinor = (activity as any).display_price_minor as number | null | undefined;
  // v2 external: the wizard writes the fee into organizer_fee_per_person_minor
  // (display_price_minor stays 0 — locked behavior); older data may carry a
  // display price instead.
  const externalPriceKr = isV2
    ? (displayPriceMinor && displayPriceMinor > 0 ? Math.round(displayPriceMinor / 100) : feeKr)
    : (activity.display_price ?? 0);

  // Financial overview (legacy rows).
  // Sprint D PR-AB Phase 4-aware: prefer open_matches.price_per_person_minor (öre)
  // over legacy price_per_person (kr). Display values stay in kr (whole-kr math
  // is the existing convention for this component).
  const _priceMoney = resolveMoney(
    (activity as any).price_per_person_minor,
    activity.price_per_person,
    (activity as any).currency
  );
  const pricePerPlayer = _priceMoney ? Math.round(_priceMoney.valueMinor / 100) : 0;
  const paidCount = players.filter(
    (p) => p.status === "joined"
  ).length;
  const collectedEstimate = paidCount * pricePerPlayer;
  const emptySpots = Math.max(0, activity.spots_total - activity.spots_filled);
  const remainingEstimate = emptySpots * pricePerPlayer;

  return (
    <Card className="border-primary/30 bg-primary/5">
      <CardHeader className="pb-2">
        <button
          className="flex items-center justify-between w-full text-left"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <CardTitle className="text-base flex items-center gap-2">
            Arrangörspanel
            <Badge variant="outline" className="text-[10px]">
              Arrangör
            </Badge>
          </CardTitle>
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </button>
      </CardHeader>

      {isExpanded && (
        <CardContent className="space-y-4">
          {/* Participant management */}
          <ParticipantList
            players={players}
            organizerId={activity.created_by}
            spotsTotal={activity.spots_total}
            spotsFilled={activity.spots_filled}
            isOrganizer={true}
            onApprove={onApprove}
            onReject={onReject}
            onRemove={onRemove}
            isApproving={isApproving}
            isRejecting={isRejecting}
            isRemoving={isRemoving}
          />

          {/* Edit description */}
          <div className="space-y-2">
            <Label htmlFor="org-description" className="text-sm font-medium">
              Meddelande / Beskrivning
            </Label>
            <Textarea
              id="org-description"
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                setDescriptionDirty(true);
              }}
              placeholder="T.ex. 'Kör avslappnat, alla nivåer välkomna'"
              rows={3}
            />
            {descriptionDirty && (
              <Button
                size="sm"
                onClick={handleSaveDescription}
                disabled={isUpdating}
              >
                {isUpdating ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                ) : (
                  <Send className="h-3.5 w-3.5 mr-1" />
                )}
                Spara
              </Button>
            )}
          </div>

          {/* Financial overview — mode-aware.
              G-023: v2 (fee-led) rows show the organizer fee from the v2 fields;
              the court share is charged per player at join (member-discount and
              pricing-rule dependent), so the panel states that honestly instead
              of inventing a per-person total. Legacy rows render as before. */}
          {paymentMode === 'system' && isV2 && (
            <div className="space-y-1 text-sm">
              <p className="font-medium text-sm">Ekonomi</p>
              <div className="bg-background rounded-lg p-3 space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t("organizerPanel.feePerPerson")}</span>
                  <span>{feeKr} kr</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t("organizerPanel.organizerRevenue", { count: paidCount })}
                  </span>
                  <span>{paidCount * feeKr} kr</span>
                </div>
                <p className="text-muted-foreground pt-1 border-t mt-1">
                  {t("organizerPanel.courtChargedPerPlayer")}
                </p>
              </div>
            </div>
          )}

          {paymentMode === 'system' && !isV2 && pricePerPlayer > 0 && (
            <div className="space-y-1 text-sm">
              <p className="font-medium text-sm">Ekonomi</p>
              <div className="bg-background rounded-lg p-3 space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Pris per person</span>
                  <span>{pricePerPlayer} kr</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Beräknat inkasserat ({paidCount} spelare)
                  </span>
                  <span>{collectedEstimate} kr</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Kvar att fylla ({emptySpots} platser)
                  </span>
                  <span>{remainingEstimate} kr</span>
                </div>
              </div>
            </div>
          )}

          {paymentMode === 'external' && (
            <div className="space-y-1 text-sm">
              <p className="font-medium text-sm">Ekonomi</p>
              <div className="bg-background rounded-lg p-3 space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {isV2 ? t("organizerPanel.feePerPerson") : "Visningspris per person"}
                  </span>
                  <span>{externalPriceKr} kr</span>
                </div>
                <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-amber-800 mt-1">
                  <span>Du ansvarar f&ouml;r banhyran</span>
                </div>
              </div>
            </div>
          )}

          {paymentMode === 'free' && (
            <div className="space-y-2 text-sm">
              <p className="font-medium text-sm">Intresse</p>
              <div className="bg-background rounded-lg p-3 space-y-1 text-xs">
                <div className="flex items-center gap-2">
                  <Users className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>{paidCount} intresseanm&auml;lda</span>
                </div>
              </div>
              {onConnectCourts && !activity.court_id && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full gap-1.5"
                  onClick={onConnectCourts}
                >
                  <Link2 className="h-3.5 w-3.5" />
                  Koppla banor
                </Button>
              )}
            </div>
          )}

          {/* Quick actions */}
          <div className="space-y-2">
            <p className="font-medium text-sm">Snabbåtgärder</p>

            {/* Close / open registration */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                <span>Stäng anmälan</span>
              </div>
              <Switch
                checked={!registrationOpen}
                onCheckedChange={(checked) => {
                  if (checked) {
                    handleCloseRegistration();
                  } else {
                    handleOpenRegistration();
                  }
                }}
                disabled={isUpdating}
              />
            </div>

            {/* Cancel activity */}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full gap-1 text-destructive border-destructive/30 hover:bg-destructive/10"
                  disabled={isCancelling}
                >
                  <XCircle className="h-3.5 w-3.5" />
                  Ställ in aktivitet
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Ställ in aktivitet?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Alla deltagare kommer att meddelas. Denna åtgärd kan inte
                    ångras.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Avbryt</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={onCancelActivity}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {isCancelling ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    ) : null}
                    Ställ in
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
